import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_PARTY_LIST, API_PARTY_CREATE, API_PARTY_GET, API_PARTY_UPDATE, API_PARTY_DELETE,
  API_PARTY_ACTIVATE, API_PARTY_DEACTIVATE,
  API_PARTY_ROLES_LIST, API_PARTY_ROLES_ASSIGN, API_PARTY_ROLES_REMOVE,
  API_PARTY_ADDRESSES_LIST, API_PARTY_ADDRESSES_CREATE, API_PARTY_ADDRESSES_UPDATE,
  API_PARTY_ADDRESSES_DELETE, API_PARTY_ADDRESSES_SET_DEFAULT,
  API_PARTY_IDENTIFIERS_LIST, API_PARTY_IDENTIFIERS_CREATE,
  API_PARTY_IDENTIFIERS_UPDATE, API_PARTY_IDENTIFIERS_DELETE,
  API_PARTY_ROLE_DEFINITIONS_LIST, API_PARTY_ROLE_DEFINITIONS_CREATE,
  API_PARTY_ROLE_DEFINITIONS_UPDATE, API_PARTY_ROLE_DEFINITIONS_DELETE,
  API_PARTY_ROLE_DEFINITIONS_ACTIVATE, API_PARTY_ROLE_DEFINITIONS_DEACTIVATE,
  API_PARTY_ADDRESS_TYPE_DEFINITIONS_LIST, API_PARTY_ADDRESS_TYPE_DEFINITIONS_CREATE,
  API_PARTY_ADDRESS_TYPE_DEFINITIONS_UPDATE, API_PARTY_ADDRESS_TYPE_DEFINITIONS_DELETE,
  API_PARTY_ADDRESS_TYPE_DEFINITIONS_ACTIVATE, API_PARTY_ADDRESS_TYPE_DEFINITIONS_DEACTIVATE,
} from '../../constants/apiConstants';

// ============================================================================
// Types
// ============================================================================

export interface PartyRole {
  id: number;
  partyId: number;
  roleCode: string;
  isActive: boolean;
  status: string;
}

// External-system identifier (sm_party_identifier) — SAP BUT0ID-style alias.
// Maps a (tenant, identifier_type, identifier_value, role?) tuple to a canonical
// sm_party_parties.id. Resolver-side enables ASN ingest to find the right party.
export interface PartyIdentifier {
  id: number;
  partyId: number;
  identifierType: string;       // one of IDENTIFIER_TYPE_OPTIONS values
  identifierValue: string;
  roleCode?: string | null;     // null = wildcard (matches any role at resolve time)
  description?: string | null;
  isActive: boolean;
  status: string;
}

// EDI-standard identifier-type catalog. Mirrors backend
// PartyIdentifier.IdentifierTypeOptions verbatim. Labels are EN-only proper
// names per feedback_app_names_verbatim.md (EDI-standard names don't translate).
export const IDENTIFIER_TYPE_OPTIONS = [
  { value: 'TLT_LONGCODE', label: 'TLT Long Code' },
  { value: 'X12_ID',       label: 'X12 ID' },
  { value: 'EDIFACT_ID',   label: 'EDIFACT ID' },
  { value: 'GLN',          label: 'GLN' },
  { value: 'DUNS',         label: 'DUNS' },
  { value: 'EORI',         label: 'EORI' },
  { value: 'VAT_ID',       label: 'VAT ID' },
] as const;

export type IdentifierTypeValue = (typeof IDENTIFIER_TYPE_OPTIONS)[number]['value'];

// Backend status code emitted when the UQ pre-check trips. Mapped to a
// localized toast in PartyFormModal so admins see "duplicate" instead of a
// raw stack.
export const IDENTIFIER_STATUS_DUPLICATE = -1011;

export interface PartyAddress {
  id: number;
  partyId: number;
  addressType: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  city?: string;
  provinceState?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
  extra?: string;
  isDefault: boolean;
  isActive: boolean;
  status: string;
}

export interface Party {
  id: number;
  guid: string;
  partitionKey: string;
  tenantId: number;
  partyCode: string;
  partyName: string;
  classification?: string;
  type?: string;
  country?: string;
  contactEmail?: string;
  contactPhone?: string;
  description?: string;
  isActive: boolean;
  status: string;
  roles: PartyRole[];
  addresses: PartyAddress[];
  createdAt?: string;
  createdBy?: string;
  modifiedAt?: string;
  modifiedBy?: string;
}

export interface PartyRoleDefinition {
  id: number;
  tenantId: number;
  roleCode: string;
  roleName: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  status: string;
}

export interface PartyAddressTypeDefinition {
  id: number;
  tenantId: number;
  typeCode: string;
  typeName: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  status: string;
}

export interface CreatePartyRequest {
  tenantId: number;
  partyCode: string;
  partyName: string;
  classification?: string;
  type?: string;
  country?: string;
  contactEmail?: string;
  contactPhone?: string;
  description?: string;
  roleCodes?: string[];
  addresses?: CreatePartyAddressInput[];
}

export interface CreatePartyAddressInput {
  addressType: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  city?: string;
  provinceState?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
  isDefault?: boolean;
}

export interface UpdatePartyRequest {
  tenantId: number;
  partyName: string;
  classification?: string;
  type?: string;
  country?: string;
  contactEmail?: string;
  contactPhone?: string;
  description?: string;
}

export interface CreateAddressRequest {
  tenantId: number;
  addressType: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  city?: string;
  provinceState?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
  extra?: string;
  isDefault?: boolean;
}

export interface UpdateAddressRequest {
  tenantId: number;
  addressType: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  city?: string;
  provinceState?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
  extra?: string;
}

export interface CreatePartyIdentifierRequest {
  tenantId: number;
  identifierType: string;
  identifierValue: string;
  roleCode?: string | null;
  description?: string | null;
}

export interface UpdatePartyIdentifierRequest {
  tenantId: number;
  identifierType: string;
  identifierValue: string;
  roleCode?: string | null;
  description?: string | null;
}

// Thrown by the identifier service methods on UQ collision so the caller can
// show a localized message instead of the generic 'failed to save' toast.
// Carries the raw status code so callers can also distinguish other server
// errors if they need to.
export class PartyIdentifierError extends Error {
  constructor(public readonly statusCode: number, message: string) {
    super(message);
    this.name = 'PartyIdentifierError';
  }
}

export interface CreateDefinitionRequest {
  tenantId: number;
  roleCode?: string;
  roleName?: string;
  typeCode?: string;
  typeName?: string;
  description?: string;
  sortOrder?: number;
}

export interface UpdateDefinitionRequest {
  tenantId: number;
  roleName?: string;
  typeName?: string;
  description?: string;
  sortOrder?: number;
}

// ============================================================================
// Party Service
// ============================================================================

export const partyService = {
  // --- Party CRUD ---

  async getParties(tenantId: number, searchText?: string, status?: string, pageNumber = 1, pageSize = 20) {
    try {
      const response = await apiClient.get(API_PARTY_LIST, {
        params: { tenantId, searchText, status, pageNumber, pageSize },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data as { parties: Party[]; totalCount: number; pageNumber: number; pageSize: number };
    } catch (error) {
      logger.error('Failed to fetch parties', error);
      throw error;
    }
  },

  async getPartyById(tenantId: number, id: number): Promise<Party> {
    try {
      const response = await apiClient.get(API_PARTY_GET(id), { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.party;
    } catch (error) {
      logger.error('Failed to fetch party', error);
      throw error;
    }
  },

  async createParty(request: CreatePartyRequest) {
    try {
      const response = await apiClient.post(API_PARTY_CREATE, request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Party created successfully');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to create party: ${msg}`);
      throw error;
    }
  },

  async updateParty(id: number, request: UpdatePartyRequest) {
    try {
      const response = await apiClient.put(API_PARTY_UPDATE(id), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Party updated successfully');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to update party: ${msg}`);
      throw error;
    }
  },

  async deleteParty(tenantId: number, id: number) {
    try {
      const response = await apiClient.delete(API_PARTY_DELETE(id), { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Party deleted successfully');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to delete party: ${msg}`);
      throw error;
    }
  },

  async activateParty(tenantId: number, id: number) {
    try {
      const response = await apiClient.put(API_PARTY_ACTIVATE(id), null, { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
    } catch (error) {
      const msg = getErrorMessage(error);
      throw new Error(msg);
    }
  },

  async deactivateParty(tenantId: number, id: number) {
    try {
      const response = await apiClient.put(API_PARTY_DEACTIVATE(id), null, { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
    } catch (error) {
      const msg = getErrorMessage(error);
      throw new Error(msg);
    }
  },

  // --- Party Roles ---

  async getPartyRoles(tenantId: number, partyId: number): Promise<PartyRole[]> {
    try {
      const response = await apiClient.get(API_PARTY_ROLES_LIST(partyId), { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.roles ?? [];
    } catch (error) {
      logger.error('Failed to fetch party roles', error);
      throw error;
    }
  },

  async assignRole(partyId: number, request: { tenantId: number; roleCode: string }) {
    try {
      const response = await apiClient.post(API_PARTY_ROLES_ASSIGN(partyId), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Role assigned');
      return response.data.data?.roles ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to assign role: ${msg}`);
      throw error;
    }
  },

  async removeRole(tenantId: number, partyId: number, roleId: number) {
    try {
      const response = await apiClient.delete(API_PARTY_ROLES_REMOVE(partyId, roleId), { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Role removed');
      return response.data.data?.roles ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to remove role: ${msg}`);
      throw error;
    }
  },

  // --- Party Addresses ---

  async getPartyAddresses(tenantId: number, partyId: number): Promise<PartyAddress[]> {
    try {
      const response = await apiClient.get(API_PARTY_ADDRESSES_LIST(partyId), { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.addresses ?? [];
    } catch (error) {
      logger.error('Failed to fetch party addresses', error);
      throw error;
    }
  },

  async createAddress(partyId: number, request: CreateAddressRequest) {
    try {
      const response = await apiClient.post(API_PARTY_ADDRESSES_CREATE(partyId), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Address added');
      return response.data.data?.addresses ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to add address: ${msg}`);
      throw error;
    }
  },

  async updateAddress(partyId: number, addressId: number, request: UpdateAddressRequest) {
    try {
      const response = await apiClient.put(API_PARTY_ADDRESSES_UPDATE(partyId, addressId), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Address updated');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to update address: ${msg}`);
      throw error;
    }
  },

  async deleteAddress(tenantId: number, partyId: number, addressId: number) {
    try {
      const response = await apiClient.delete(API_PARTY_ADDRESSES_DELETE(partyId, addressId), { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Address removed');
      return response.data.data?.addresses ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to remove address: ${msg}`);
      throw error;
    }
  },

  async setDefaultAddress(tenantId: number, partyId: number, addressId: number) {
    try {
      const response = await apiClient.put(API_PARTY_ADDRESSES_SET_DEFAULT(partyId, addressId), null, { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Default address set');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to set default address: ${msg}`);
      throw error;
    }
  },

  // --- Party Identifiers ---

  async getPartyIdentifiers(tenantId: number, partyId: number): Promise<PartyIdentifier[]> {
    try {
      const response = await apiClient.get(API_PARTY_IDENTIFIERS_LIST(partyId), { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.identifiers ?? [];
    } catch (error) {
      logger.error('Failed to fetch party identifiers', error);
      throw error;
    }
  },

  async createPartyIdentifier(partyId: number, request: CreatePartyIdentifierRequest): Promise<PartyIdentifier[]> {
    try {
      const response = await apiClient.post(API_PARTY_IDENTIFIERS_CREATE(partyId), request);
      // Backend returns Result<T> with succeeded:false + a structured statusCode
      // (e.g. -1011 Duplicate_Identifier). Throw a typed error so the modal can
      // map the duplicate case to the localized toast without parsing strings.
      if (!response.data.succeeded) {
        throw new PartyIdentifierError(
          response.data.statusCode ?? response.data.code ?? 0,
          response.data.messages?.join(', ') ?? 'Failed to add identifier'
        );
      }
      toast.success('Identifier added');
      return response.data.data?.identifiers ?? [];
    } catch (error) {
      if (error instanceof PartyIdentifierError) throw error;
      const msg = getErrorMessage(error);
      toast.error(`Failed to add identifier: ${msg}`);
      throw error;
    }
  },

  async updatePartyIdentifier(partyId: number, identifierId: number, request: UpdatePartyIdentifierRequest): Promise<PartyIdentifier[]> {
    try {
      const response = await apiClient.put(API_PARTY_IDENTIFIERS_UPDATE(partyId, identifierId), request);
      if (!response.data.succeeded) {
        throw new PartyIdentifierError(
          response.data.statusCode ?? response.data.code ?? 0,
          response.data.messages?.join(', ') ?? 'Failed to update identifier'
        );
      }
      toast.success('Identifier updated');
      return response.data.data?.identifiers ?? [];
    } catch (error) {
      if (error instanceof PartyIdentifierError) throw error;
      const msg = getErrorMessage(error);
      toast.error(`Failed to update identifier: ${msg}`);
      throw error;
    }
  },

  async deletePartyIdentifier(tenantId: number, partyId: number, identifierId: number): Promise<PartyIdentifier[]> {
    try {
      const response = await apiClient.delete(API_PARTY_IDENTIFIERS_DELETE(partyId, identifierId), { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Identifier removed');
      return response.data.data?.identifiers ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to remove identifier: ${msg}`);
      throw error;
    }
  },

  // --- Role Definitions ---

  async getRoleDefinitions(tenantId: number, activeOnly = true): Promise<PartyRoleDefinition[]> {
    try {
      const response = await apiClient.get(API_PARTY_ROLE_DEFINITIONS_LIST, { params: { tenantId, activeOnly } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.definitions ?? [];
    } catch (error) {
      logger.error('Failed to fetch role definitions', error);
      throw error;
    }
  },

  async createRoleDefinition(request: { tenantId: number; roleCode: string; roleName: string; description?: string; sortOrder?: number }) {
    try {
      const response = await apiClient.post(API_PARTY_ROLE_DEFINITIONS_CREATE, request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Role definition created');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to create role definition: ${msg}`);
      throw error;
    }
  },

  async updateRoleDefinition(id: number, request: { tenantId: number; roleName: string; description?: string; sortOrder?: number }) {
    try {
      const response = await apiClient.put(API_PARTY_ROLE_DEFINITIONS_UPDATE(id), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Role definition updated');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to update role definition: ${msg}`);
      throw error;
    }
  },

  async deleteRoleDefinition(tenantId: number, id: number) {
    try {
      const response = await apiClient.delete(API_PARTY_ROLE_DEFINITIONS_DELETE(id), { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Role definition deleted');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to delete role definition: ${msg}`);
      throw error;
    }
  },

  async activateRoleDefinition(tenantId: number, id: number) {
    try {
      const response = await apiClient.put(API_PARTY_ROLE_DEFINITIONS_ACTIVATE(id), null, { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to activate role definition: ${msg}`);
      throw error;
    }
  },

  async deactivateRoleDefinition(tenantId: number, id: number) {
    try {
      const response = await apiClient.put(API_PARTY_ROLE_DEFINITIONS_DEACTIVATE(id), null, { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to deactivate role definition: ${msg}`);
      throw error;
    }
  },

  // --- Address Type Definitions ---

  async getAddressTypeDefinitions(tenantId: number, activeOnly = true): Promise<PartyAddressTypeDefinition[]> {
    try {
      const response = await apiClient.get(API_PARTY_ADDRESS_TYPE_DEFINITIONS_LIST, { params: { tenantId, activeOnly } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.definitions ?? [];
    } catch (error) {
      logger.error('Failed to fetch address type definitions', error);
      throw error;
    }
  },

  async createAddressTypeDefinition(request: { tenantId: number; typeCode: string; typeName: string; description?: string; sortOrder?: number }) {
    try {
      const response = await apiClient.post(API_PARTY_ADDRESS_TYPE_DEFINITIONS_CREATE, request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Address type definition created');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to create address type definition: ${msg}`);
      throw error;
    }
  },

  async updateAddressTypeDefinition(id: number, request: { tenantId: number; typeName: string; description?: string; sortOrder?: number }) {
    try {
      const response = await apiClient.put(API_PARTY_ADDRESS_TYPE_DEFINITIONS_UPDATE(id), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Address type definition updated');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to update address type definition: ${msg}`);
      throw error;
    }
  },

  async deleteAddressTypeDefinition(tenantId: number, id: number) {
    try {
      const response = await apiClient.delete(API_PARTY_ADDRESS_TYPE_DEFINITIONS_DELETE(id), { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Address type definition deleted');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to delete address type definition: ${msg}`);
      throw error;
    }
  },

  async activateAddressTypeDefinition(tenantId: number, id: number) {
    try {
      const response = await apiClient.put(API_PARTY_ADDRESS_TYPE_DEFINITIONS_ACTIVATE(id), null, { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to activate address type definition: ${msg}`);
      throw error;
    }
  },

  async deactivateAddressTypeDefinition(tenantId: number, id: number) {
    try {
      const response = await apiClient.put(API_PARTY_ADDRESS_TYPE_DEFINITIONS_DEACTIVATE(id), null, { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to deactivate address type definition: ${msg}`);
      throw error;
    }
  },
};
